import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
} from "react";
import { X } from "lucide-react";
import { color, motion as motionTokens, radius, space } from "@tokens/index";
import { IconButton } from "@primitives/IconButton";
import { Popover } from "@primitives/overlay";
import { Text } from "@primitives/Text";
import { TextInput } from "@primitives/TextInput";
import { MIN_SEARCH_LENGTH, type EntityCandidate } from "./types";

/**
 * EntitySearchField: combobox over the entity candidates of one type.
 *
 * The candidate list is a `Popover` anchored to the input. Escape with the
 * list open closes the list and consumes the event, so the host dialog
 * stays open. Escape with the list closed reaches the dialog.
 *
 * The field owns its query and list state. The host remounts it (by key)
 * when the entity type changes.
 */

export interface EntitySearchFieldProps {
  readonly entityType: string;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly selected: EntityCandidate | null;
  readonly candidates: readonly EntityCandidate[];
  readonly candidatesLoading: boolean;
  readonly message: string | null;
  readonly messageTone: "muted" | "danger";
  readonly onSearch: (type: string, query: string) => void;
  readonly onSelect: (candidate: EntityCandidate) => void;
  readonly onClear: () => void;
}

// Tonal only: the selected entity reads as a filled row, not an outlined one.
const selectedRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[2],
  minHeight: 40,
  paddingInline: `${space[3]} ${space[1]}`,
  background: color.bg.selected,
  borderRadius: radius.md,
};

const fieldStyle: CSSProperties = {
  display: "grid",
  gap: space[1],
  minWidth: 0,
};

const listStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: space[1],
};

function optionStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minHeight: 40,
    minWidth: 0,
    padding: `${space[2]} ${space[3]}`,
    borderRadius: radius.md,
    background: active ? color.bg.selected : "transparent",
    cursor: "pointer",
    transition: `background ${motionTokens.durations.fast} ${motionTokens.easings.standard}`,
  };
}

export function EntitySearchField({
  entityType,
  inputRef,
  selected,
  candidates,
  candidatesLoading,
  message,
  messageTone,
  onSearch,
  onSelect,
  onClear,
}: EntitySearchFieldProps): ReactElement {
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const baseId = useId();
  const inputId = `${baseId}-input`;
  const labelId = `${baseId}-label`;
  const listId = `${baseId}-list`;
  const messageId = `${baseId}-message`;

  const listOpen = open && query.length >= MIN_SEARCH_LENGTH;
  const hasOptions = listOpen && !candidatesLoading && candidates.length > 0;

  useEffect(() => {
    if (!hasOptions || listRef.current === null) return;
    const activeItem = listRef.current.children[activeIndex] as HTMLElement | undefined;
    activeItem?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, hasOptions]);

  const close = (): void => {
    setOpen(false);
  };

  const select = (candidate: EntityCandidate): void => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    onSelect(candidate);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      if (listOpen) {
        event.preventDefault();
        close();
      }
      return;
    }
    if (!hasOptions) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev < candidates.length - 1 ? prev + 1 : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : candidates.length - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const candidate = candidates[activeIndex];
      if (candidate !== undefined) select(candidate);
    }
  };

  const messageNode =
    message !== null ? (
      <Text as="p" id={messageId} role="caption" tone={messageTone}>
        {message}
      </Text>
    ) : null;

  if (selected !== null) {
    return (
      <div style={fieldStyle}>
        <Text as="span" id={labelId} role="bodySm" weight="medium" tone="muted">
          Search {entityType}
        </Text>
        <div
          role="group"
          aria-labelledby={labelId}
          aria-describedby={message !== null ? messageId : undefined}
          style={selectedRowStyle}
        >
          <Text as="span" role="body" weight="semibold" truncate style={{ flex: 1 }}>
            {selected.displayName}
            <Text as="span" role="body" tone="muted">
              {" \u00b7 "}
              {selected.id}
            </Text>
          </Text>
          <IconButton
            type="button"
            icon={X}
            aria-label="Clear entity"
            tooltip="Clear entity"
            variant="ghost"
            size="md"
            onClick={onClear}
          />
        </div>
        {messageNode}
      </div>
    );
  }

  return (
    <div style={fieldStyle}>
      <Text as="label" id={labelId} role="bodySm" weight="medium" tone="muted" htmlFor={inputId}>
        Search {entityType}
      </Text>
      <div ref={anchorRef}>
        <TextInput
          id={inputId}
          ref={inputRef}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listOpen ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={hasOptions ? `${listId}-${String(activeIndex)}` : undefined}
          aria-describedby={message !== null ? messageId : undefined}
          autoComplete="off"
          placeholder={"Search by name or ID\u2026"}
          value={query}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            setOpen(true);
            setActiveIndex(0);
            onSearch(entityType, next);
          }}
          onFocus={() => {
            if (query.length >= MIN_SEARCH_LENGTH) setOpen(true);
          }}
          onBlur={close}
          onKeyDown={handleKeyDown}
          fullWidth
        />
      </div>
      <Popover
        id={listId}
        role="listbox"
        labelledBy={labelId}
        open={listOpen}
        onClose={close}
        anchorRef={anchorRef}
        matchAnchorWidth
        maxHeight="32dvh"
        initialFocus="none"
        returnFocus={false}
      >
        <div
          ref={listRef}
          style={listStyle}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          {candidatesLoading ? (
            <div role="status" style={{ padding: `${space[2]} ${space[3]}` }}>
              <Text role="bodySm" tone="muted">
                {"Searching\u2026"}
              </Text>
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: `${space[2]} ${space[3]}` }}>
              <Text role="bodySm" tone="muted">
                No results found.
              </Text>
            </div>
          ) : (
            candidates.map((candidate, index) => (
              <div
                key={candidate.id}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === activeIndex}
                style={optionStyle(index === activeIndex)}
                onClick={() => {
                  select(candidate);
                }}
                onMouseEnter={() => {
                  setActiveIndex(index);
                }}
              >
                <Text role="body" weight="semibold" truncate>
                  {candidate.displayName}
                </Text>
                {candidate.subtitle != null && (
                  <Text role="caption" tone="muted" truncate>
                    {candidate.subtitle}
                  </Text>
                )}
              </div>
            ))
          )}
        </div>
      </Popover>
      {messageNode}
    </div>
  );
}
